import primaryComponents from "../../../../components/primaryComponents";
import secondaryComponents from "../../../../components/secondaryComponents";
import { useAccountingCashFlow } from "./useAccountingCashFlow";
import { formatCurrency, defaultDateOnlyDisplay } from "../../../../helpers";
import styles from "./styles.module.scss";

const AccountingCashFlow = () => {
  const {
    startDate,
    endDate,
    businessName,
    reportData,
    isLoadingReport,
    storeList,
    storeHasMultipleCurrency,
    currentStore,
    setDateFilter,
    filterByStoreId,
    onGenerateReport,
  } = useAccountingCashFlow();

  const currencyCode = reportData?.currency_code || "NGN";

  const formatAmount = ({ value = 0, isBracket = false }) => {
    const formattedValue = formatCurrency({
      value: isBracket ? Math.abs(value) : value,
      currencyCode,
    });

    return isBracket ? `(${formattedValue})` : formattedValue;
  };

  const renderRow = ({
    label,
    value,
    className = "",
    amountClassName = "",
    showNegativeWhen = false,
    showBracketWhen = false,
  }) => {
    const isNegative = showNegativeWhen;
    const useBracket = showBracketWhen;

    return (
      <tr className={className}>
        <td className={styles.lineItem__name}>{label}</td>
        <td
          className={`${styles.lineItem__amount} ${amountClassName} ${isNegative ? styles.lineItem__negative : ""}`}
        >
          {formatAmount({ value, isBracket: useBracket })}
        </td>
      </tr>
    );
  };

  const shouldShow = (value, alwaysShow = false) => {
    if (alwaysShow) return true;
    return Number(value || 0) !== 0;
  };

  const isPositive = (value) => Number(value || 0) > 0;
  const isNegative = (value) => Number(value || 0) < 0;

  const operatingActivityRows = [
    {
      key: "depreciation_and_amortization",
      label: "Depreciation and Amortization",
      value: reportData?.depreciation_and_amortization,
    },
    {
      key: "account_receivables_changes",
      label: "Account Receivable",
      value: reportData?.account_receivables_changes,
      showNegativeWhen: isPositive(reportData?.account_receivables_changes),
      showBracketWhen: isPositive(reportData?.account_receivables_changes),
    },
    {
      key: "inventory_changes",
      label: "Inventory",
      value: reportData?.inventory_changes,
      showNegativeWhen: isPositive(reportData?.inventory_changes),
      showBracketWhen: isPositive(reportData?.inventory_changes),
    },
    {
      key: "prepaid_expenses_changes",
      label: "Prepaid Expenses",
      value: reportData?.prepaid_expenses_changes,
      showNegativeWhen: isPositive(reportData?.prepaid_expenses_changes),
      showBracketWhen: isPositive(reportData?.prepaid_expenses_changes),
    },
    {
      key: "vat_receivable_changes",
      label: "Vat Receivables",
      value: reportData?.vat_receivable_changes,
      showNegativeWhen: isPositive(reportData?.vat_receivable_changes),
      showBracketWhen: isPositive(reportData?.vat_receivable_changes),
    },
    {
      key: "account_payables_changes",
      label: "Account Payables",
      value: reportData?.account_payables_changes,
    },
    {
      key: "vat_payable_changes",
      label: "Vat Payables",
      value: reportData?.vat_payable_changes,
    },
    {
      key: "accrued_expenses_changes",
      label: "Accrued Expenses",
      value: reportData?.accrued_expenses_changes,
    },
  ];

  const investingRows = [
    {
      key: "fixed_assets",
      label: "Fixed Asset",
      value: reportData?.fixed_assets,
      showNegativeWhen: isNegative(reportData?.fixed_assets),
      showBracketWhen: isNegative(reportData?.fixed_assets),
    },
    {
      key: "intangible_assets",
      label: "Intangible Asset",
      value: reportData?.intangible_assets,
      showNegativeWhen: isNegative(reportData?.intangible_assets),
      showBracketWhen: isNegative(reportData?.intangible_assets),
    },
  ];

  const financingRows = [
    {
      key: "owners_capital",
      label: "Owners Capital",
      value: reportData?.owners_capital,
    },
    {
      key: "owners_drawings",
      label: "Owners Drawing",
      value: reportData?.owners_drawings,
      showNegativeWhen: isPositive(reportData?.owners_drawings),
      showBracketWhen: isPositive(reportData?.owners_drawings),
    },
    {
      key: "short_term_liabilities",
      label: "Short Term Liabilities",
      value: reportData?.short_term_liabilities,
      showNegativeWhen: isPositive(reportData?.short_term_liabilities),
      showBracketWhen: isPositive(reportData?.short_term_liabilities),
    },
    {
      key: "long_term_liabilities",
      label: "Long Term Liabilities",
      value: reportData?.long_term_liabilities,
      showNegativeWhen: isPositive(reportData?.long_term_liabilities),
      showBracketWhen: isPositive(reportData?.long_term_liabilities),
    },
  ];

  const isOperatingNetNegative = isNegative(
    reportData?.net_cash_from_operating_activities,
  );
  const isInvestingNetNegative = isNegative(
    reportData?.net_cash_from_investing_activities,
  );
  const isFinancingNetNegative = isNegative(
    reportData?.net_cash_from_financing_activities,
  );

  return (
    <>
      <div className="d-flex justify-content-between p-3">
        <div style={{ width: "200px" }}>
          <primaryComponents.SelectField
            options={
              storeHasMultipleCurrency
                ? [...storeList]
                : [
                    {
                      name: "All stores",
                      id: "ALL_STORES",
                    },
                    ...storeList,
                  ]
            }
            idKey="id"
            nameKey="name"
            onChange={(value) => {
              filterByStoreId(value);
            }}
            value={
              storeList?.find(
                (storeItem) => `${storeItem?.id}` === `${currentStore}`,
              )?.name || "All stores"
            }
            floatError={false}
            classNames="store-select"
          />
        </div>
        <div className="d-flex align-items-end" style={{ gap: "12px" }}>
          <div>
            <label htmlFor="">Date Range</label>
            <primaryComponents.DateDefinedRangeInput
              startDate={startDate}
              endDate={endDate}
              onChange={setDateFilter}
              showPlaceholder={true}
            />
          </div>
          <div>
            <primaryComponents.Button
              classNames="btn btn--primary"
              onClick={onGenerateReport}
              isLoading={isLoadingReport}
              loadingText="Generating..."
            >
              Generate Report
            </primaryComponents.Button>
          </div>
        </div>
      </div>

      <secondaryComponents.LoaderHelper
        isLoading={isLoadingReport}
        classNames="mt-4"
      >
        {!reportData ? (
          <div className={styles.emptyState}>
            Set a date range and click Generate Report to view your Cash Flow
            Statement.
          </div>
        ) : (
          <div className={styles.reportCard}>
            <div className={styles.reportHeader}>
              <div className={styles.reportHeader__title}>{businessName}</div>
              <div className={styles.reportHeader__business}>
                Cash Flow Statement
              </div>
              <div className={styles.reportHeader__meta}>
                From {defaultDateOnlyDisplay(reportData?.start_date)} to{" "}
                {defaultDateOnlyDisplay(reportData?.end_date)}
              </div>
            </div>

            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.reportTable__totalCol}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className={styles.subtotalRow}>
                  <td>Beginning Cash Balance</td>
                  <td>
                    {formatAmount({ value: reportData?.opening_cash_balance })}
                  </td>
                </tr>

                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Cash Flow from Operating Activities</td>
                </tr>

                {renderRow({
                  label: "Net Income",
                  value: reportData?.net_income,
                  className: styles.subHeaderRow,
                })}

                {operatingActivityRows
                  .filter((row) => shouldShow(row.value))
                  .map((row) => (
                    <tr key={row.key} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>{row.label}</td>
                      <td
                        className={`${styles.lineItem__amount} ${row.showNegativeWhen ? styles.lineItem__negative : ""}`}
                      >
                        {formatAmount({
                          value: row.value,
                          isBracket: row.showBracketWhen,
                        })}
                      </td>
                    </tr>
                  ))}

                {renderRow({
                  label: "Net Cash Flow from Operating Activities",
                  value: reportData?.net_cash_from_operating_activities,
                  className: styles.subtotalRow,
                  showNegativeWhen: isOperatingNetNegative,
                  showBracketWhen: isOperatingNetNegative,
                })}

                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Cash Flow from Investing Activities</td>
                </tr>

                {investingRows
                  .filter((row) => shouldShow(row.value))
                  .map((row) => (
                    <tr key={row.key} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>{row.label}</td>
                      <td
                        className={`${styles.lineItem__amount} ${row.showNegativeWhen ? styles.lineItem__negative : ""}`}
                      >
                        {formatAmount({
                          value: row.value,
                          isBracket: row.showBracketWhen,
                        })}
                      </td>
                    </tr>
                  ))}

                {renderRow({
                  label: "Net Cash Flow from Investing Activities",
                  value: reportData?.net_cash_from_investing_activities,
                  className: styles.subtotalRow,
                  showNegativeWhen: isInvestingNetNegative,
                  showBracketWhen: isInvestingNetNegative,
                })}

                <tr className={styles.thinDivider}>
                  <td colSpan={2} />
                </tr>

                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>Cash Flow from Financing Activities</td>
                </tr>

                {financingRows
                  .filter((row) => shouldShow(row.value))
                  .map((row) => (
                    <tr key={row.key} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>{row.label}</td>
                      <td
                        className={`${styles.lineItem__amount} ${row.showNegativeWhen ? styles.lineItem__negative : ""}`}
                      >
                        {formatAmount({
                          value: row.value,
                          isBracket: row.showBracketWhen,
                        })}
                      </td>
                    </tr>
                  ))}

                {renderRow({
                  label: "Net Cash Flow from Financing Activities",
                  value: reportData?.net_cash_from_financing_activities,
                  className: styles.subtotalRow,
                  showNegativeWhen: isFinancingNetNegative,
                  showBracketWhen: isFinancingNetNegative,
                })}

                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                <tr className={styles.netProfitRow}>
                  <td>Ending Cash Balance</td>
                  <td>
                    {formatAmount({ value: reportData?.closing_cash_balance })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </secondaryComponents.LoaderHelper>
    </>
  );
};

export default AccountingCashFlow;
